import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PaginatedResponse } from 'app/_models/paginated-data.model';
import { Pagination } from 'app/_models/pagination.model';
import { SiteFormResponse } from 'app/_models/site-forms/site-form-response.model';
import { SiteForm } from 'app/_models/site-forms/site-form.model';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs-compat';
import { catchError, map } from 'rxjs/operators';
import { BaseHttpClientService } from './base-http-client.service';
import { SiteFormResponseFactory } from 'app/_models/workflow-items/site-form-response.factory';

@Injectable()
export class SiteFormsService extends BaseHttpClientService {
  constructor(
    private http: HttpClient,
    private siteFormResponseFactory: SiteFormResponseFactory,
  ) {
    super();
  }

  // --- Forms

  public getAllSiteForms(pagination: Pagination, sort: string[], search?: { key: 'siteIdFilter'; value: string }[]): Observable<PaginatedResponse<SiteForm>> {
    const params = this.createQueryStrings(pagination, sort, search);
    const headers = this.applyLatestOnlyHeader();

    return (this.http.get(environment.apiUrl + `api/ext-site-form`, { params, observe: 'response', headers }) as any)
      .pipe(map((response) => this.handlePaginatedData(response as HttpResponse<any>, SiteForm)))
      .pipe(catchError(this.handleError));
  }

  public getAllBySite(siteId: number, pagination: Pagination, sort: string[], search?: { key: 'siteIdFilter'; value: string }[]): Observable<PaginatedResponse<SiteForm>> {
    const params = this.createQueryStrings(pagination, sort, search);
    const headers = this.applyLatestOnlyHeader();

    return (this.http.get(environment.apiUrl + `api/ext-site-form/by-site/${siteId}`, { params, observe: 'response', headers }) as any)
      .pipe(map((response) => this.handlePaginatedData(response as HttpResponse<any>, SiteForm)))
      .pipe(catchError(this.handleError));
  }

  public getSiteForm(formId: number): Observable<SiteForm> {
    return this.http
      .get(environment.apiUrl + `api/ext-site-form/${formId}`)
      .pipe(map((response) => this.handleData(response as HttpResponse<any>, SiteForm)))
      .pipe(catchError(this.handleError));
  }

  public createForm(body): Observable<SiteForm> {
    return (this.http.post(environment.apiUrl + `api/ext-site-form`, body) as any).pipe(map((response) => this.handleData(response as HttpResponse<any>, SiteForm))).pipe(catchError(this.handleError));
  }

  public updateForm(formId, body): Observable<SiteForm> {
    return (this.http.put(environment.apiUrl + `api/ext-site-form/${formId}`, body) as any)
      .pipe(map((response) => this.handleData(response as HttpResponse<any>, SiteForm)))
      .pipe(catchError(this.handleError));
  }

  public deleteForm(formId: number): Observable<any> {
    return this.http.delete(environment.apiUrl + `api/ext-site-form/${formId}`).pipe(catchError(this.handleError));
  }



  // --- Form Responses

  public submitFormResponse(body) {
    return this.http.post(environment.apiUrl + 'api/ext-site-form-response', body)
      .pipe(map((response) => this.handleData(response as HttpResponse<any>, SiteFormResponse)))
      .pipe(catchError(this.handleError));
  }

  public submitDocumentUploadForFormResponse(documents: any[]): Observable<string[]> {
    // multipart/form-data, append each part
    let formData = new FormData();

    documents.forEach((blob, index) => {
      formData.append('files', blob, documents[index].fileName);
    });

    // HTTP POST
    try {
      return this.http.post(environment.apiUrl + 'api/ext-site-form-response/files', formData)
          .pipe(map(response => (response as any[]).map(file => file.fileName)));

    } catch (error) {
      if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
        throw new Error('File is infected and cannot be uploaded');
      }
      return this.handleError(error);
    }
  }

  public getAllSiteFormResponsesForForm(formId: number, pagination: Pagination, sort: string[], search?: any): Observable<PaginatedResponse<SiteFormResponse>> {
    const params = this.createQueryStrings(pagination, sort, search);
    const headers = this.applyLatestOnlyHeader();

    return (this.http.get(environment.apiUrl + `api/ext-site-form-response/by-site-form/${formId}`, { params, observe: 'response', headers }) as any)
      .pipe(map((response) => this.handlePaginatedData(response as HttpResponse<any>, SiteFormResponse)))
      .pipe(catchError(this.handleError));
  }

  public getSiteFormReponse(responseId: number): Observable<SiteFormResponse> {
    return this.http
      .get(environment.apiUrl + `api/ext-site-form-response/${responseId}`)
      .pipe(map((response) => this.siteFormResponseFactory.createAndFetchImages(response)))
      .pipe(catchError(this.handleError));
  }

  public getAllResponsesByWorkOrderId(workOrderId: number, pagination: Pagination, sort: string[]): Observable<PaginatedResponse<SiteFormResponse>> {
    const params = this.createQueryStrings(pagination, sort);
    return (this.http.get(environment.apiUrl + `api/ext-site-form-response/by-work-order/${workOrderId}`, { params, observe: 'response' }) as any)
      .pipe(map((response) => this.handlePaginatedData(response as HttpResponse<any>, SiteFormResponse)))
      .pipe(catchError(this.handleError));
  }

  public getAllResponsesByUserSessionId(userSessionId: number, pagination: Pagination, sort: string[]): Observable<PaginatedResponse<SiteFormResponse>> {
    const params = this.createQueryStrings(pagination, sort);
    return (this.http.get(environment.apiUrl + `api/ext-site-form-response/by-user-session/${userSessionId}`, { params, observe: 'response' }) as any)
      .pipe(map((response) => {
        return this.handlePaginatedData(response as HttpResponse<any>);
      }))
      .pipe(map(paginationResponse => {
        (paginationResponse as any).body = (paginationResponse as any).body.map((item: any) => this.siteFormResponseFactory.createAndFetchImages(item));
        return paginationResponse;
      }))
      .pipe(catchError(this.handleError));
  }

  public getSiteOwnerLogo(siteOwnerLogin: string): string {
    let logoURL: string;
    switch (siteOwnerLogin) {
      case 'weholdings':
        logoURL = './assets/img/form-logos/form-logo-weholdings.png';
        break;
      case 'statkraft':
        logoURL = './assets/img/form-logos/form-logo-statkraft.png';
        break;
      case 'adman':
        logoURL = './assets/img/form-logos/form-logo-adman.png';
        break;
      case 'energia':
        logoURL = './assets/img/form-logos/form-logo-energia.png';
        break;
      case 'rengen':
        logoURL = './assets/img/form-logos/form-logo-rengen.png';
        break;
      case 'adman':
        logoURL = './assets/img/form-logos/form-logo-adman.png';
        break;
      case 'ventusenergy':
        logoURL = './assets/img/form-logos/form-logo-ventusenergy.png';
        break;
      case 'wfsoireland':
        logoURL = './assets/img/form-logos/form-logo-wfsoireland.png';
        break;
      case 'energypro':
        logoURL = './assets/img/form-logos/form-logo-energypro.png';
        break;
      default:
        logoURL = './assets/img/form-logos/form-logo-skylark-owner.png';
        break;
    }
    return logoURL;
  }
}
